import React, { useEffect, useMemo, useRef, useState, memo } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { AgGridReact } from 'ag-grid-react';
import { decamelize } from 'humps';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';

import config from '../../config';
import downloadFile from '../../utils/downloadFile';
import { getRetainerHealth } from '../../actions/retainerHealthReport';

import { LevelInputHeaderCell } from './LevelInputHeaderCell';
import { DateRender } from './DateRender';
import { Filter, initialValues } from './Filter';
import Button from '../helpers/Button';
import { AppPage } from '../AppPage';
import { Card } from '../helpers/Card';
import { GridCard } from '../helpers/GridCard';
import { DisplayColumns, columns } from './DisplayColumns';
import { showError } from '../../utils/MessagePopup';
import { usePopupContext } from '../../contexts/PopupContext';

import style from './style.module.css';

const RED = '#ffb3b3';
const GREEN = '#cef9ce';

const getLevelStyle = (value, level) => {
  if (value > level) {
    return { backgroundColor: GREEN, textAlign: 'right' };
  }

  return { backgroundColor: RED, textAlign: 'right' };
};

const initialBenchmarks = {
  approvalRatePercent: 65,
  contactedRatePercent: 50,
  leadsRatePercent: 25,
  conferenceCallsRatePercent: 15,
  visitsRatePercent: 5,
};

const RetainerHealthReportComponent = ({ retainerHealth, loading, location, userRole, getRetainerHealth }) => {
  const { openPopup } = usePopupContext();
  const gridRef = useRef(null);

  const [baseSearchParams, setBaseSearchParams] = useState(undefined);
  const [weightsParams, setWeightsParams] = useState(new URLSearchParams(initialValues));
  const [hideFields, setHideFields] = useState(columns);
  const [widthNormalized, setWidthNormalized] = useState(0);
  const [benchmarks, setBenchmarks] = useState(initialBenchmarks);
  const [isDisabledExport, setDisabledExport] = useState(false);

  useEffect(() => {
    if (loading && gridRef.current) {
      gridRef.current.api.showLoadingOverlay();
    } else {
      gridRef.current.api.hideOverlay();
    }
  }, [loading]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    const moduleIds = searchParams.getAll('module_ids');
    const userIds = searchParams.getAll('user_ids');

    moduleIds
      .flatMap(item => item.split(','))
      .forEach(moduleId => {
        searchParams.append('module_ids[]', moduleId);
      });
    searchParams.delete('module_ids');
    userIds
      .flatMap(item => item.split(','))
      .forEach(userId => {
        searchParams.append('user_ids[]', userId);
      });
    searchParams.delete('user_ids');

    if (searchParams.has('from')) {
      searchParams.append('date_range[from]', searchParams.get('from'));
      searchParams.delete('from');
    }

    if (searchParams.has('to')) {
      searchParams.append('date_range[to]', searchParams.get('to'));
      searchParams.delete('to');
    }

    getRetainerHealth({ query: searchParams.toString() });
    setBaseSearchParams(searchParams);
  }, []);

  const reportInfo = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);

    const current = searchParams.has('current') ? `Current ${searchParams.get('current')}; ` : '';
    const modules = `${searchParams.get('module_text')} modules; `;
    const users = searchParams.has('user_text') ? `${searchParams.get('user_text')} users; ` : '';
    const projectType = `${searchParams.get('project_type')} Project Type`;

    return `${current}${modules}${users}${projectType}`;
  }, []);

  const isReadOnlyLevelInputCell = useMemo(() => ![config.ADMINISTRATOR, config.FINANCE_ADMIN].includes(userRole), [
    userRole,
  ]);

  const handleExportClick = () => {
    const searchParams = new URLSearchParams(baseSearchParams);

    if (hideFields.length < columns.length) {
      hideFields.forEach(value => {
        searchParams.append(`hide_fields[]`, decamelize(value));
      });
    }

    if (!isReadOnlyLevelInputCell) {
      weightsParams.forEach((value, key) => {
        searchParams.append(`weights[${key}]`, value);
      });

      Object.entries(benchmarks).forEach(value => {
        searchParams.append(`benchmarks[${decamelize(value[0])}]`, value[1]);
      });
    }

    searchParams.append('width_normalized', widthNormalized.toString());

    downloadFile({
      url: `${config.API_BASE_URL}/api/v1/reports/retainer_health/export`,
      params: searchParams.toString(),
      onError: ({ data }) => {
        showError(openPopup, data.errors);
      },
    });
  };

  const handleFilterClick = formData => {
    const searchParams = new URLSearchParams(baseSearchParams);
    const weightsParams = new URLSearchParams();

    Array.from(formData).forEach(value => {
      searchParams.append(`weights[${value[0]}]`, value[1]);
      weightsParams.append(value[0], value[1]);
    });

    setWeightsParams(weightsParams);
    setDisabledExport(false);
    getRetainerHealth({ query: searchParams.toString() });
  };

  const handleFilterChange = () => {
    setDisabledExport(true);
  };

  const handleHideFields = fields => {
    setHideFields(fields);
  };

  const handleWidthNormalizedChange = event => {
    setWidthNormalized(event.target.checked === true ? 1 : 0);
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: 'switchColumns',
        headerGroupComponentFramework: DisplayColumns,
        headerGroupComponentParams: params => ({
          columnApi: params.columnApi,
          onHideFields: handleHideFields,
        }),
        minWidth: 300,
        children: [
          {
            headerName: 'Retainer\nHealth',
            field: 'rating',
            pinned: 'left',
            cellStyle: params => {
              const v = Math.max(0, Math.min(1, params.value));
              const v2 = v ** 0.5;
              // 120° → Green
              const hue = v2 * 120;

              return {
                background: `hsl(${hue}deg 60% 55%)`,
                textAlign: 'right',
              };
            },
            maxWidth: 100,
            valueFormatter: params => params.node.rowIndex + 1,
          },
          {
            headerName: 'Client',
            field: 'client',
            pinned: 'left',
          },
          {
            headerName: 'Search',
            field: 'search',
            pinned: 'left',
          },
          {
            headerName: 'Contractual Name',
            field: 'contractualName',
            pinned: 'left',
          },
        ],
      },
      {
        children: [
          { headerName: 'Fund', field: 'fund' },
          { headerName: 'Module', field: 'modules' },
          { headerName: 'DM1', field: 'dealmakers1' },
          { headerName: 'DM2', field: 'dealmakers2' },
          { headerName: 'Type', field: 'projectType', maxWidth: 140 },
          {
            headerName: 'Current O&B',
            field: 'hasOfferAndBetter',
            cellStyle: params => (params.value === false ? { backgroundColor: RED } : { backgroundColor: GREEN }),
            valueFormatter: params => (params.value === false ? 'No' : 'Yes'),
            maxWidth: 130,
          },
          {
            headerName: 'Closed\n Deals',
            field: 'closedDeals',
            cellStyle: { textAlign: 'center' },
            maxWidth: 90,
          },
        ],
      },
      {
        headerName: 'Retainer Length',
        headerClass: style.retainerHeaderCell,
        children: [
          {
            headerName: 'Retainer\n Start Date',
            field: 'startDate',
            cellRendererFramework: DateRender,
            cellStyle: { textAlign: 'right' },
            maxWidth: 120,
          },
          {
            headerName: 'Months\n as Client',
            field: 'monthsAsClient',
            cellClass: style.greyCell,
            cellStyle: { textAlign: 'right' },
            maxWidth: 100,
          },
          {
            headerName: 'Years\n as Client',
            field: 'yearsAsClient',
            cellClass: style.greyCell,
            cellStyle: { textAlign: 'right' },
            maxWidth: 100,
          },
        ],
      },
      {
        headerName: 'Approval List Statistics',
        headerClass: style.approvalListStatisticsHeaderCell,
        children: [
          {
            headerName: 'Approval\n Lists Sent',
            field: 'approvalListsSent',
            cellStyle: { textAlign: 'right' },
            maxWidth: 120,
          },
          {
            headerName: 'Approval\n Lists per Month',
            field: 'approvalListsPerMonth',
            cellClass: style.greyCell,
            cellStyle: { textAlign: 'right' },
            maxWidth: 130,
          },
          {
            headerName: 'Date of last\n sent approval list',
            field: 'lastApprovalListsSent',
            cellRendererFramework: DateRender,
            cellStyle: { textAlign: 'right' },
            maxWidth: 140,
          },
          {
            headerName: 'Days since\n last approval list',
            field: 'daysLastApprovalListsSent',
            maxWidth: 150,
            cellStyle: params =>
              params.value < 40
                ? { backgroundColor: GREEN, textAlign: 'right' }
                : { backgroundColor: RED, textAlign: 'right' },
          },
        ],
      },
      {
        children: [
          {
            headerName: 'Targets\n Sent',
            headerClass: style.brownHeaderCell,
            field: 'targetsSent',
            cellStyle: { textAlign: 'right' },
            maxWidth: 95,
          },
          {
            headerName: 'Targets\n Approved',
            headerClass: style.brownHeaderCell,
            field: 'targetsApproved',
            cellStyle: { textAlign: 'right' },
            maxWidth: 110,
          },
          {
            headerName: 'Approval Rate',
            headerClass: style.brownHeaderCell,
            field: 'approvalRatePercent',
            valueFormatter: params => `${params.value}%`,
            headerComponentFramework: LevelInputHeaderCell,
            headerComponentParams: params => ({
              setFilter: value =>
                setBenchmarks(prev => ({
                  ...prev,
                  approvalRatePercent: value,
                })),
              api: params.api,
              defaultValue: benchmarks.approvalRatePercent,
              readOnly: isReadOnlyLevelInputCell,
            }),
            cellStyle: params => getLevelStyle(params.value, params.context.benchmarks.approvalRatePercent),
            maxWidth: 170,
          },
          {
            headerName: 'Targets\n contacted',
            headerClass: style.brownHeaderCell,
            field: 'targetsContacted',
            cellStyle: { textAlign: 'right' },
            maxWidth: 110,
          },
          {
            headerName: 'Contact Rate',
            headerClass: style.brownHeaderCell,
            field: 'contactedRatePercent',
            valueFormatter: params => `${params.value}%`,
            headerComponentFramework: LevelInputHeaderCell,
            headerComponentParams: params => ({
              setFilter: value =>
                setBenchmarks(prev => ({
                  ...prev,
                  contactedRatePercent: value,
                })),
              api: params.api,
              defaultValue: benchmarks.contactedRatePercent,
              readOnly: isReadOnlyLevelInputCell,
            }),
            cellStyle: params => getLevelStyle(params.value, params.context.benchmarks.contactedRatePercent),
            maxWidth: 170,
          },
          {
            headerName: 'Leads',
            headerClass: style.brownHeaderCell,
            field: 'leads',
            cellStyle: { textAlign: 'right' },
            maxWidth: 90,
          },
          {
            headerName: 'Lead Rate',
            headerClass: style.brownHeaderCell,
            field: 'leadsRatePercent',
            valueFormatter: params => `${params.value}%`,
            headerComponentFramework: LevelInputHeaderCell,
            headerComponentParams: params => ({
              setFilter: value =>
                setBenchmarks(prev => ({
                  ...prev,
                  leadsRatePercent: value,
                })),
              api: params.api,
              defaultValue: benchmarks.leadsRatePercent,
              readOnly: isReadOnlyLevelInputCell,
            }),
            cellStyle: params => getLevelStyle(params.value, params.context.benchmarks.leadsRatePercent),
            maxWidth: 170,
          },
          {
            headerName: 'Conf.\n Calls',
            headerClass: style.brownHeaderCell,
            field: 'conferenceCalls',
            cellStyle: { textAlign: 'right' },
            maxWidth: 80,
          },
          {
            headerName: 'CC Rate',
            headerClass: style.brownHeaderCell,
            field: 'conferenceCallsRatePercent',
            valueFormatter: params => `${params.value}%`,
            headerComponentFramework: LevelInputHeaderCell,
            headerComponentParams: params => ({
              setFilter: value =>
                setBenchmarks(prev => ({
                  ...prev,
                  conferenceCallsRatePercent: value,
                })),
              api: params.api,
              defaultValue: benchmarks.conferenceCallsRatePercent,
              readOnly: isReadOnlyLevelInputCell,
            }),
            cellStyle: params => getLevelStyle(params.value, params.context.benchmarks.conferenceCallsRatePercent),
            maxWidth: 170,
          },
          {
            headerName: 'Visits',
            headerClass: style.brownHeaderCell,
            field: 'visits',
            cellStyle: { textAlign: 'right' },
            maxWidth: 90,
          },
          {
            headerName: 'Visit Rate',
            headerClass: style.brownHeaderCell,
            field: 'visitsRatePercent',
            valueFormatter: params => `${params.value}%`,
            headerComponentFramework: LevelInputHeaderCell,
            headerComponentParams: params => ({
              setFilter: value =>
                setBenchmarks(prev => ({
                  ...prev,
                  visitsRatePercent: value,
                })),
              api: params.api,
              defaultValue: benchmarks.visitsRatePercent,
              readOnly: isReadOnlyLevelInputCell,
            }),
            cellStyle: params => getLevelStyle(params.value, params.context.benchmarks.visitsRatePercent),
            maxWidth: 170,
          },
          {
            headerName: 'O&B',
            headerClass: style.brownHeaderCell,
            field: 'offersAndBetter',
            cellStyle: { textAlign: 'right' },
            maxWidth: 80,
          },
          {
            headerName: 'Total O&B',
            headerClass: style.brownHeaderCell,
            field: 'totalOffersAndBetter',
            cellStyle: { textAlign: 'right' },
            maxWidth: 80,
          },
          {
            headerName: 'Total\nO&B Rate',
            headerClass: style.brownHeaderCell,
            field: 'totalOffersAndBetterRatePercent',
            valueFormatter: params => `${params.value}%`,
            cellStyle: { textAlign: 'right' },
            maxWidth: 120,
          },
        ],
      },
    ],
    [],
  );

  const toolbar = (
    <>
      <div className="checkbox-wrap mr8">
        <input
          checked={widthNormalized === 1}
          id="widthNormalized"
          onChange={handleWidthNormalizedChange}
          type="checkbox"
        />
        <label htmlFor="widthNormalized">Include normalized values</label>
      </div>
      <div className={style.toolbar}>
        <Button
          disabled={isDisabledExport}
          onClick={handleExportClick}
          title={isDisabledExport ? 'Please apply changes before exporting' : ''}
        >
          <i aria-hidden="true" className="fa fa-download" /> Export
        </Button>
      </div>
    </>
  );

  return (
    <AppPage overline="Reports" title="Retainer Health Report" toolbar={toolbar}>
      <Card title="Weighting">
        <p className="help-block mt0">Report is based on: {reportInfo}</p>
        <Filter
          loading={loading}
          onChange={handleFilterChange}
          onFilter={handleFilterClick}
          readOnly={isReadOnlyLevelInputCell}
        />
      </Card>
      <GridCard className={style.grid}>
        <div className={classNames(style.reportGrid, 'ag-theme-alpine')}>
          <AgGridReact
            ref={gridRef}
            columnDefs={columnDefs}
            context={{ benchmarks }}
            defaultColDef={{
              sortable: true,
              filter: true,
              resizable: true,
            }}
            domLayout="autoHeight"
            headerHeight={40}
            rowData={retainerHealth.size === 0 ? null : retainerHealth.toJS()}
            rowHeight={24}
            suppressMovableColumns
            suppressRowHoverHighlight
          />
        </div>
      </GridCard>
    </AppPage>
  );
};

const mapStateToProps = state => ({
  location: state.router.location,
  retainerHealth: state.retainerHealthReport.get('queryResults'),
  loading: state.retainerHealthReport.get('loading'),
  userRole: state.auth.getIn(['user', 'roles', 0, 'slug']),
});

const mapActionToProps = {
  getRetainerHealth,
};

const RetainerHealthReport = connect(mapStateToProps, mapActionToProps)(memo(RetainerHealthReportComponent));

export { RetainerHealthReport };
